import * as turf from '@turf/turf'
import { LocateFixed } from 'lucide-react'
import { ComponentProps, useCallback, useEffect, useRef, useState } from 'react'
import { MapRef, ViewStateChangeEvent } from 'react-map-gl'
import { twMerge } from 'tailwind-merge'

import { useCalculatePerimeter } from '@/hooks/use-calculate-perimeter'
import { PerimeterCoordinatesResponse } from '@/models/perimeter-coordinates'
import { brazilCoordinates } from '@/utils/brazil-coordinates'

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip'
import { MapLayerPerimeter } from './map-layer-perimeter'
import { MapRoot } from './map-root'
import { MapScale } from './map-scale'

interface BoundsEndResponse {
  latitude: number
  longitude: number
  zoom: number
}

interface PerimeterMapProps extends ComponentProps<'div'> {
  perimeterCoordinates: PerimeterCoordinatesResponse
  hiddenCredits?: boolean
  isReposition?: boolean
  onRepositionDone?: () => void
}

export function PerimeterMap({
  perimeterCoordinates,
  hiddenCredits = true,
  isReposition = false,
  onRepositionDone,
  className,
  ...props
}: PerimeterMapProps) {
  const mapRef = useRef<MapRef>(null)
  const { bounds, polygonCoordinates, polygonGeoJSON } =
    useCalculatePerimeter(perimeterCoordinates)

  const [areaCalculated, setAreaCalculated] = useState<number | null>(null)
  const [shouldReposition, setShouldReposition] = useState<boolean>(false)
  const boundsEnd = useRef<BoundsEndResponse | null>(null)

  const onLoadMap = useCallback(() => {
    if (!mapRef.current) return

    const map = mapRef.current.getMap()

    const fitBounds = () => {
      map.fitBounds(
        [
          [bounds.west, bounds.south],
          [bounds.east, bounds.north],
        ],
        {
          padding: 150,
          animate: true,
          duration: 5000,
          maxZoom: 15,
          essential: true,
        },
      )
    }

    if (map.isStyleLoaded()) {
      fitBounds()
    } else {
      map.once('style.load', fitBounds)
    }

    map.once('moveend', (e) => {
      const { target } = e
      const center = target.getCenter()
      const zoom = target.getZoom()

      boundsEnd.current = {
        zoom,
        latitude: center.lat,
        longitude: center.lng,
      }
    })
  }, [bounds])

  useEffect(() => {
    if (isReposition && onRepositionDone) {
      const map = mapRef.current?.getMap()
      if (!map || polygonCoordinates[0]?.length < 3) return

      onLoadMap()
      setShouldReposition(false)
      boundsEnd.current = null
      onRepositionDone()
    }
  }, [isReposition, onLoadMap, onRepositionDone, polygonCoordinates])

  const returnCalculatedAreaTotalFromPolygon = useCallback(() => {
    const area = turf.area(polygonGeoJSON)

    const areaInHectares = turf.convertArea(area, 'meters', 'hectares')

    const truncateAreaInHectares = Math.floor(areaInHectares * 100) / 100

    setAreaCalculated(truncateAreaInHectares)
  }, [polygonGeoJSON])

  useEffect(() => {
    if (polygonCoordinates[0]?.length < 3) return

    returnCalculatedAreaTotalFromPolygon()
  }, [polygonCoordinates, returnCalculatedAreaTotalFromPolygon])

  function handleOnMoveEnd(e: ViewStateChangeEvent) {
    if (!boundsEnd.current || polygonCoordinates[0]?.length < 3) {
      return
    }

    const { viewState } = e
    const { latitude, longitude, zoom } = boundsEnd.current

    const latEnd = viewState.latitude
    const lngEnd = viewState.longitude
    const zoomEnd = viewState.zoom

    const isDifferentZoom = zoom !== zoomEnd
    const isDifferentLocale = latitude !== latEnd && longitude !== lngEnd

    if (isDifferentZoom || isDifferentLocale) {
      setShouldReposition(true)
    }
  }

  function handleRepositionPolygon() {
    if (!mapRef.current) return

    onLoadMap()
    mapRef.current.once('moveend', () => {
      setShouldReposition(false)
    })
  }

  return (
    <div
      className={twMerge(
        'h-previewImage relative *:w-full *:rounded-lg',
        className,
      )}
      {...props}
    >
      <MapRoot
        ref={mapRef}
        onLoad={onLoadMap}
        onMoveEnd={handleOnMoveEnd}
        attributionControl={hiddenCredits}
        initialViewState={brazilCoordinates}
        mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
        data-testid="perimeter-map-root"
      >
        <MapScale />

        <MapLayerPerimeter polygonGeoJSON={polygonGeoJSON} />

        {areaCalculated && (
          <div className="border-bios-primary text-monochromatic-500 absolute top-2 right-2 rounded-2xl border bg-white p-2 text-sm font-medium select-none">
            <span>Área total calculada: {areaCalculated}ha</span>
          </div>
        )}

        {shouldReposition && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  onClick={handleRepositionPolygon}
                  className="group bg-monochromatic-300 absolute top-2 left-2 z-10 cursor-pointer flex-col items-center justify-center rounded-2xl p-2"
                  data-testid="perimeter-map-reposition-button"
                >
                  <LocateFixed className="text-monochromatic-500 group-hover:text-monochromatic-400 size-8 font-medium transition-colors duration-300" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                Reposicionar o foco do mapa para o perímetro
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </MapRoot>
    </div>
  )
}
